<template>
  <div class="mof-root" :style="themePageStyle">
    <head>
      <meta charset="utf-8" /><title>FLOCKR | My Offers</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <link rel="shortcut icon" href="/assets/images/favicon.svg">
      <link href="/assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="/assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="/assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="/assets/css/custom.css" rel="stylesheet" type="text/css" />
    </head>
    <!-- was <body> — nested body breaks the DOM (document already has one) -->
    <div :data-sidebar="darkMode ? 'dark' : 'light'">
    <div id="layout-wrapper">
      <nav class="mof-topbar">
        <div class="container d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center" style="gap:16px;">
            <back-button class="me-2" />
            <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
            <span class="fw-semibold" style="color:var(--fm-h,#1A1A1A);font-size:15px;">My Offers</span>
          </div>
          <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Marketplace</button>
        </div>
      </nav>

      <div class="container mof-container">
        <!-- Tabs -->
        <div class="mof-tabs mb-3">
          <button class="mof-tab" :class="{active:tab==='sent'}" @click="switchTab('sent')">
            <i class="mdi mdi-send me-1"></i>My Offers
            <span v-if="unreadSent" class="mof-dot"></span>
          </button>
          <button class="mof-tab" :class="{active:tab==='received'}" @click="switchTab('received')">
            <i class="mdi mdi-inbox-arrow-down me-1"></i>From Buyers
            <span v-if="unreadReceived" class="mof-dot"></span>
          </button>
        </div>

        <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div></div>

        <div v-else-if="offers.length === 0" class="text-center py-5">
          <i class="mdi mdi-handshake-outline" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
          <h5 class="mt-3" style="color:var(--fm-h,#1A1A1A);">{{ tab === 'received' ? 'No offers from buyers yet' : 'You haven\'t made any offers yet' }}</h5>
          <p style="color:var(--fm-s,#777);">{{ tab === 'received' ? 'When buyers send offers on your listings, they\'ll show up here as a chat thread you can negotiate in.' : 'Find something you like and tap "Make Offer" — it\'ll appear here so you can negotiate.' }}</p>
          <button v-if="tab==='sent'" class="btn btn-warning mt-2" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Browse Marketplace</button>
        </div>

        <div v-else>
          <div v-for="o in offers" :key="'o-'+o.id" class="mof-thread" :class="{'mof-thread-focus': focusOfferId === String(o.id)}" :ref="'offer-'+o.id">

            <!-- Thread header: listing + status -->
            <div class="mof-thread-head" @click="$router.push('/marketplace/listing/'+o.listing_id)">
              <div class="d-flex align-items-center" style="gap:10px;">
                <i class="mdi mdi-tag-outline" style="color:#BFA164;font-size:16px;"></i>
                <span class="mof-thread-title">{{ o.listing_title || 'Listing' }}</span>
              </div>
              <span class="mof-badge" :class="'st-'+o.status">{{ statusLabel(o.status) }}</span>
            </div>

            <!-- Conversation bubbles -->
            <div class="mof-conv">

              <!-- Full negotiation thread — one bubble per round, nothing overwritten -->
              <div v-for="(ev,ei) in threadEvents(o)" :key="'ev-'+o.id+'-'+ei" class="mof-bubble-row" :class="{mine: isMine(ev.actor_id), theirs: !isMine(ev.actor_id)}">
                <div class="mof-bub-avatar">{{ evInitials(o, ev) }}</div>
                <div class="mof-bubble">
                  <div class="mof-bub-meta">
                    <i v-if="ev.event_type==='counter'" class="mdi mdi-swap-horizontal me-1" style="color:#f1b44c;"></i>{{ evAuthor(o, ev) }} {{ ev.event_type==='counter' ? 'countered' : 'offered' }} · {{ fmtTime(ev.created_at) }}
                  </div>
                  <div class="mof-bub-price">{{ (o.currency || 'KES') }} {{ formatPrice(ev.price) }}<span v-if="ev.event_type==='offer' && o.quantity>1" class="mof-bub-qty"> × {{ o.quantity }}</span></div>
                  <div v-if="ev.message" class="mof-bub-msg">{{ ev.message }}</div>
                </div>
              </div>

              <!-- Accepted / rejected closing line -->
              <div v-if="o.status==='accepted'" class="mof-conv-closer accepted">
                <i class="mdi mdi-check-decagram me-1"></i>Deal agreed at KES {{ formatPrice(o.counter_price || o.offered_price) }}
              </div>
              <div v-else-if="o.status==='rejected'" class="mof-conv-closer rejected">
                <i class="mdi mdi-close-octagon me-1"></i>This offer didn't go through
              </div>
              <div v-else-if="o.status==='expired'" class="mof-conv-closer rejected">
                <i class="mdi mdi-timer-off-outline me-1"></i>Offer expired with no response
              </div>
            </div>

            <!-- Action row — adapts to whose turn it is -->
            <div class="mof-actions" v-if="needsMyAction(o)">
              <div v-if="!counterModeFor[o.id]" class="d-flex gap-2 flex-wrap">
                <button class="btn btn-sm btn-success flex-fill" @click="respond(o,'accept')">
                  <i class="mdi mdi-check me-1"></i>{{ acceptLabel(o) }}
                </button>
                <button class="btn btn-sm btn-outline-warning flex-fill" @click="openCounter(o)">
                  <i class="mdi mdi-swap-horizontal me-1"></i>Counter
                </button>
                <button class="btn btn-sm mof-chat-btn" @click="openOfferChat(o)" :title="'Chat with '+otherPartyName(o)">
                  <i class="mdi mdi-message-text-outline"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="respond(o,'reject')" :title="'Decline'">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>

              <!-- Inline counter composer -->
              <div v-else class="mof-counter-form">
                <div class="d-flex gap-2 mb-2">
                  <input type="number" class="form-control form-control-sm" placeholder="Your counter price (KES)" v-model="counterForm[o.id].price" :style="themeInputStyle" />
                </div>
                <textarea class="form-control form-control-sm mb-2" rows="2" placeholder="Optional message (e.g. 'I can do this, transport included')" v-model="counterForm[o.id].message" :style="themeInputStyle"></textarea>
                <div class="d-flex gap-2">
                  <button class="btn btn-sm btn-warning text-dark fw-bold flex-fill" :disabled="!counterForm[o.id].price" @click="sendCounter(o)">
                    <i class="mdi mdi-send me-1"></i>Send Counter
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" @click="cancelCounter(o)">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Waiting state — last move was mine -->
            <div v-else-if="o.status==='pending' || o.status==='countered'" class="mof-waiting">
              <i class="mdi mdi-clock-outline me-1"></i>Waiting for {{ waitingFor(o) }} to respond
            </div>

            <!-- Accepted: checkout (buyer) or "they'll pay" (seller) -->
            <div v-else-if="o.status==='accepted'" class="mof-actions">
              <button v-if="isMine(o.buyer_id)" class="btn btn-warning text-dark fw-bold w-100" @click="$router.push('/checkout/'+o.id+'?listingId='+o.listing_id)">
                <i class="mdi mdi-lock me-1"></i>Proceed to Checkout — KES {{ formatPrice(o.counter_price || o.offered_price) }}
              </button>
              <div v-else class="text-center" style="color:#50C878;font-size:12px;padding:6px 0;">
                <i class="mdi mdi-clock-outline me-1"></i>Waiting for buyer to pay
              </div>
            </div>

            <!-- Always-available chat footer — lets either party talk + share files/photos/videos any time -->
            <div class="mof-chat-foot" @click="openOfferChat(o)">
              <i class="mdi mdi-message-text-outline me-2"></i>
              <span>Chat with {{ otherPartyName(o) }}</span>
              <span class="mof-chat-foot-attach" :title="'Share photos, videos or documents'">
                <i class="mdi mdi-paperclip"></i>
              </span>
            </div>

          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>
<style>
html[data-theme="light"] body { background:#F7F5F2!important; color:#1A1A1A!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style scoped>
.mof-root{min-height:100vh}
.mof-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"] :root{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
.mof-container{padding-top:80px;padding-bottom:60px;max-width:760px}

.mof-tabs{display:flex;gap:8px;position:relative}
.mof-tab{padding:8px 20px;border-radius:20px;border:1px solid var(--fm-br);background:var(--fm-bg1)!important;color:var(--fm-m)!important;font-size:13px;cursor:pointer;transition:all .2s;position:relative}
.mof-tab:hover{border-color:#BFA164;color:var(--fm-h)!important}
.mof-tab.active{background:rgba(191,161,100,.15)!important;border-color:#BFA164;color:#BFA164!important;font-weight:600}
.mof-dot{position:absolute;top:6px;right:8px;width:8px;height:8px;border-radius:50%;background:#FF6B6B;box-shadow:0 0 0 2px var(--fm-bg1)}

/* THREAD CARD */
.mof-thread{background:var(--fm-bg1)!important;border:1px solid var(--fm-br);border-radius:14px;margin-bottom:16px;overflow:hidden}
.mof-thread-head{display:flex;align-items:center;justify-content:space-between;padding:12px 16px;background:var(--fm-bg2)!important;border-bottom:1px solid var(--fm-br);cursor:pointer;transition:background .15s}
.mof-thread-head:hover{background:var(--fm-bg3)!important}
.mof-thread-title{font-weight:600;color:var(--fm-h);font-size:13px}

.mof-badge{padding:3px 10px;border-radius:12px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.4px}
.st-pending{background:rgba(241,180,76,.12)!important;color:#f1b44c!important}
.st-accepted{background:rgba(80,200,120,.14)!important;color:#50C878!important}
.st-rejected{background:rgba(255,107,107,.12)!important;color:#FF6B6B!important}
.st-countered{background:rgba(108,180,238,.14)!important;color:#6CB4EE!important}
.st-expired{background:rgba(107,99,86,.2)!important;color:var(--fm-s)!important}

/* CONVERSATION */
.mof-conv{padding:16px 14px 6px;background:var(--fm-bg1)}
.mof-bubble-row{display:flex;align-items:flex-end;gap:8px;margin-bottom:12px}
.mof-bubble-row.mine{flex-direction:row-reverse}
.mof-bub-avatar{width:32px;height:32px;border-radius:50%;background:var(--fm-bg3);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;color:#BFA164;flex-shrink:0}
.mof-bubble{max-width:78%;padding:9px 13px;border-radius:14px;background:var(--fm-bg2);border:1px solid var(--fm-br);position:relative}
.mof-bubble-row.mine .mof-bubble{background:rgba(191,161,100,.12);border-color:rgba(191,161,100,.35);border-bottom-right-radius:4px}
.mof-bubble-row.theirs .mof-bubble{border-bottom-left-radius:4px}
.mof-bub-meta{font-size:10px;color:var(--fm-s);margin-bottom:3px}
.mof-bub-price{font-size:16px;font-weight:700;color:#BFA164;line-height:1.2}
.mof-bub-qty{font-size:11px;color:var(--fm-s);font-weight:400;margin-left:3px}
.mof-bub-msg{margin-top:5px;font-size:12.5px;color:var(--fm-h);line-height:1.4;word-break:break-word}

.mof-conv-closer{text-align:center;padding:8px 0;font-size:12px;font-weight:600;border-top:1px dashed var(--fm-br);margin-top:4px}
.mof-conv-closer.accepted{color:#50C878}
.mof-conv-closer.rejected{color:var(--fm-s)}

/* ACTIONS */
.mof-actions{padding:12px 14px 14px;border-top:1px solid var(--fm-br);background:var(--fm-bg1)}
.mof-counter-form{padding:6px 0}
.mof-counter-form .form-control{background:var(--fm-bg2);border-color:var(--fm-br);color:var(--fm-h)}

.mof-waiting{text-align:center;padding:10px;font-size:12px;color:var(--fm-s);background:var(--fm-bg2);border-top:1px solid var(--fm-br)}

/* Chat button (inline with accept/counter row) */
.mof-chat-btn{background:rgba(108,180,238,.12)!important;border:1px solid rgba(108,180,238,.4)!important;color:#6CB4EE!important;width:38px;flex-shrink:0;padding:0!important;display:flex;align-items:center;justify-content:center}
.mof-chat-btn:hover{background:rgba(108,180,238,.22)!important;color:#fff!important}
.mof-chat-btn .mdi{font-size:18px}

/* Always-on chat footer — appears on every thread regardless of status */
.mof-chat-foot{display:flex;align-items:center;justify-content:flex-start;gap:6px;padding:9px 14px;border-top:1px solid var(--fm-br);background:var(--fm-bg2);font-size:12.5px;color:#6CB4EE;cursor:pointer;transition:background .15s,color .15s}
.mof-chat-foot:hover{background:rgba(108,180,238,.08);color:#fff}
.mof-chat-foot-attach{margin-left:auto;color:var(--fm-s);font-size:13px}
.mof-chat-foot:hover .mof-chat-foot-attach{color:#BFA164}

/* Focused-thread highlight for deep-links from notifications */
.mof-thread-focus{border-color:#BFA164!important;box-shadow:0 0 0 2px rgba(191,161,100,.25);animation:mofFocusFlash 1.6s ease-out 1}
@keyframes mofFocusFlash{
  0%{box-shadow:0 0 0 0 rgba(191,161,100,.55)}
  100%{box-shadow:0 0 0 2px rgba(191,161,100,.25)}
}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .mof-thread { padding: 12px; border-radius: 10px; }
  .mof-tabs { gap: 4px; }
  .mof-tab { padding: 7px 11px; font-size: 12px; }
  .mof-bubble { max-width: 88%; padding: 8px 11px; font-size: 13px; }
  .mof-composer-inner { padding: 8px; gap: 6px; }
  .mof-composer textarea { font-size: 14px; }
  .mof-chat-foot { padding: 10px 12px; font-size: 13px; }
  .mof-page { padding-left: 12px !important; padding-right: 12px !important;
              padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)); }
}
</style>
<script>
import { executeJson, flocker } from "@/api";
export default {
  name: "MyOffersPage",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:true,
    tab:'sent',
    offers:[],
    myId:'',
    counterModeFor:{},   // { offerId: true }
    counterForm:{},      // { offerId: {price, message} }
    unreadReceived:false,
    unreadSent:false,
    focusOfferId:'',
  }},
  mounted(){
    this.syncTheme();
    this.themeObserver = new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.myId = sessionStorage.getItem('userId');
    if(!this.myId){this.$router.push('/login');return;}
    // Deep-link: /my-offers?offer=ID lands directly on a specific offer thread.
    // Notifications use this so "Counter-offer received" jumps right to the conversation.
    this.focusOfferId = String(this.$route.query.offer || '');
    if(this.focusOfferId){
      // Try sent first; if the focused offer isn't ours, fall through to received.
      this.loadAndFocus();
    } else {
      this.fetchSent();
    }
  },
  beforeUnmount(){
    if(this.themeObserver){this.themeObserver.disconnect()}
  },
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},
  },
  methods:{
    syncTheme(){
      var t=document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark';
      if(window.__flockrSetTheme) window.__flockrSetTheme(t);
      this.darkMode=t!=='light';
      if(document.body){
        document.body.setAttribute('data-sidebar',this.darkMode?'dark':'light');
        document.body.style.background=this.darkMode?'#0A0A0A':'#F7F5F2';
        document.body.style.color=this.darkMode?'#F5F0E8':'#1A1A1A';
      }
    },
    switchTab(t){
      this.tab=t;
      this.counterModeFor={};
      if(t==='received') this.fetchReceived(); else this.fetchSent();
    },
    async loadAndFocus(){
      // Try the Sent list first
      this.tab = 'sent';
      await this.fetchSent();
      if(this.offers.some(o => String(o.id) === this.focusOfferId)){
        this.$nextTick(this.scrollToFocused);
        return;
      }
      // Not in Sent — try Received
      this.tab = 'received';
      await this.fetchReceived();
      this.$nextTick(this.scrollToFocused);
    },
    scrollToFocused(){
      if(!this.focusOfferId) return;
      var refKey = 'offer-' + this.focusOfferId;
      var nodes = this.$refs[refKey];
      var el = Array.isArray(nodes) ? nodes[0] : nodes;
      if(el && el.scrollIntoView){
        el.scrollIntoView({behavior:'smooth', block:'center'});
      }
    },
    async fetchReceived(){
      this.loading=true;
      try{
        var r=await executeJson({function:"getListingOffers",sellerId:this.myId},flocker);
        if(r.data.success) this.offers=r.data.data.offers||[];
      }catch(e){console.error(e)} this.loading=false;
    },
    async fetchSent(){
      this.loading=true;
      try{
        var r=await executeJson({function:"getListingOffers",buyerId:this.myId},flocker);
        if(r.data.success) this.offers=r.data.data.offers||[];
      }catch(e){console.error(e)} this.loading=false;
    },

    // === Identity & turn helpers ===
    isMine(uid){return uid && String(uid) === String(this.myId)},
    threadEvents(o){
      var evs = (o.events || []).filter(function(e){ return e.event_type === 'offer' || e.event_type === 'counter'; });
      if(evs.length) return evs;
      // Legacy rows created before the events table — synthesize what we still have
      var out = [{actor_id:o.buyer_id, event_type:'offer', price:o.offered_price, message:o.message, created_at:o.created_at}];
      if(o.counter_price) out.push({actor_id:o.last_action_by || o.seller_id, event_type:'counter', price:o.counter_price, message:o.counter_message, created_at:o.responded_at || o.updated_at});
      return out;
    },
    evAuthor(o, ev){
      if(this.isMine(ev.actor_id)) return 'You';
      if(String(ev.actor_id) === String(o.seller_id)) return o.seller_name || 'Seller';
      return o.buyer_name || 'Buyer';
    },
    evInitials(o, ev){
      if(this.isMine(ev.actor_id)) return 'YO';
      if(String(ev.actor_id) === String(o.seller_id)) return this.getInitials(o.seller_name);
      return this.getInitials(o.buyer_name);
    },
    needsMyAction(o){
      if(o.status === 'rejected' || o.status === 'expired' || o.status === 'accepted') return false;
      // pending: only the seller (listing owner) can act
      if(o.status === 'pending') return this.isMine(o.seller_id);
      // countered: whoever did NOT make the latest counter responds next
      if(o.status === 'countered'){
        var lastBy = o.last_action_by || o.seller_id; // legacy default
        return !this.isMine(lastBy);
      }
      return false;
    },
    waitingFor(o){
      if(o.status === 'pending') return this.isMine(o.seller_id) ? 'yourself' : (o.seller_name || 'the seller');
      if(o.status === 'countered'){
        var lastBy = o.last_action_by || o.seller_id;
        if(this.isMine(lastBy)){
          // I countered last — waiting for the other side
          return lastBy === o.seller_id ? (o.buyer_name || 'the buyer') : (o.seller_name || 'the seller');
        }
      }
      return 'the other side';
    },
    acceptLabel(o){
      var price = o.counter_price ? Number(o.counter_price) : Number(o.offered_price);
      return 'Accept at KES ' + this.formatPrice(price);
    },
    otherPartyName(o){
      if(this.isMine(o.buyer_id)) return o.seller_name || 'the seller';
      return o.buyer_name || 'the buyer';
    },
    openOfferChat(o){
      // Pop the global ChatDock anchored to this listing's conversation. Same component
      // used elsewhere in the app — supports photos, videos and documents.
      var otherId = this.isMine(o.buyer_id) ? o.seller_id : o.buyer_id;
      var otherName = this.otherPartyName(o);
      var otherAvatar = this.isMine(o.buyer_id) ? o.seller_avatar : o.buyer_avatar;
      window.dispatchEvent(new CustomEvent('flockr-open-chat', {detail:{
        userId: otherId,
        name: otherName,
        avatar: otherAvatar,
        contextType: 'listing',
        contextId: o.listing_id,
        listingId: o.listing_id
      }}));
    },

    // === Actions ===
    async respond(o,action){
      try{
        var r=await executeJson({function:"respondToOffer",offerId:o.id,action:action,respondedBy:this.myId},flocker);
        if(r.data.success){
          if(action==='accept') o.status='accepted';
          else if(action==='reject') o.status='rejected';
          o.last_action_by = this.myId;
        } else if(r.data.message){
          alert(r.data.message);
        }
      }catch(e){console.error(e); alert('Could not save your response. Try again?')}
    },
    openCounter(o){
      if(!this.counterForm[o.id]){
        this.counterForm[o.id] = {price:'', message:''};
      }
      this.counterModeFor = Object.assign({}, this.counterModeFor, {[o.id]:true});
    },
    cancelCounter(o){
      var m = Object.assign({}, this.counterModeFor);
      delete m[o.id];
      this.counterModeFor = m;
    },
    async sendCounter(o){
      var f = this.counterForm[o.id];
      if(!f || !f.price) return;
      try{
        var r=await executeJson({
          function:"respondToOffer", offerId:o.id, action:'counter',
          counterPrice:f.price, counterMessage:f.message||'', respondedBy:this.myId
        },flocker);
        if(r.data.success){
          // Seed from the synthesized legacy thread BEFORE mutating counter_price,
          // otherwise the original-offer bubble vanishes on pre-events rows
          if(!o.events || !o.events.length) o.events = this.threadEvents(o).slice();
          o.status = 'countered';
          o.counter_price = f.price;
          o.counter_message = f.message || '';
          o.last_action_by = this.myId;
          o.responded_at = new Date().toISOString();
          o.events.push({actor_id:this.myId, event_type:'counter', price:f.price, message:f.message || '', created_at:new Date().toISOString()});
          this.cancelCounter(o);
        } else if(r.data.message){
          alert(r.data.message);
        }
      }catch(e){console.error(e); alert('Could not send counter. Try again?')}
    },

    // === Formatting ===
    formatPrice(a){return a?Number(a).toLocaleString('en-KE'):'0'},
    fmtTime(d){
      if(!d) return '';
      var dt = new Date(d);
      var now = new Date();
      var ms = now - dt;
      if(ms < 60000) return 'just now';
      if(ms < 3600000) return Math.floor(ms/60000)+'m ago';
      if(ms < 86400000) return Math.floor(ms/3600000)+'h ago';
      if(ms < 604800000) return Math.floor(ms/86400000)+'d ago';
      return dt.toLocaleDateString('en-KE',{day:'numeric',month:'short'});
    },
    getInitials(n){if(!n)return'?';return n.split(' ').map(function(w){return w[0]}).join('').substring(0,2).toUpperCase()},
    statusLabel(s){
      var map = {pending:'New offer', countered:'Negotiating', accepted:'Agreed', rejected:'Declined', expired:'Expired'};
      return map[s] || s;
    },
  }
};
</script>
